/* Extraliebe Terminbuchung – liest Termin-Varianten (SKU TYP|Standort|Datum|Uhrzeit)
   und bucht über den Shopify-Warenkorb/Checkout. Keine externen Abhängigkeiten. */
(() => {
  'use strict';

  const TZ = 'Europe/Berlin';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ── Zeit-Helfer ────────────────────────────────────────────────────────────
  const partsFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  function offsetMs(t) {
    const p = Object.fromEntries(partsFmt.formatToParts(new Date(t)).map((x) => [x.type, x.value]));
    return Date.UTC(+p.year, p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - Math.floor(t / 1000) * 1000;
  }
  /** "2026-10-24", "10:00" (Berliner Zeit) → Zeitstempel */
  function berlinToMs(date, time) {
    const [y, m, d] = date.split('-').map(Number);
    const [h, mi] = time.split(':').map(Number);
    const guess = Date.UTC(y, m - 1, d, h, mi);
    let t = guess - offsetMs(guess);
    const t2 = guess - offsetMs(t);
    return t2 !== t ? t2 : t;
  }
  const fmt = (opts) => new Intl.DateTimeFormat('de-DE', { timeZone: TZ, ...opts });
  const fWeekdayShort = fmt({ weekday: 'short' });
  const fDay = fmt({ day: 'numeric' });
  const fMonthShort = fmt({ month: 'short' });
  const fMonthLong = fmt({ month: 'long', year: 'numeric' });
  const fFull = fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const fShortDate = fmt({ weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  const noon = (date) => berlinToMs(date, '12:00');

  function money(cents) {
    const v = cents / 100;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency', currency: 'EUR', minimumFractionDigits: v % 1 ? 2 : 0,
    }).format(v);
  }

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const slug = (s) => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '-');

  // ── Daten aufbereiten ──────────────────────────────────────────────────────
  // Antwort des Termin-Dienstes in das interne Format bringen
  function slotsFromApi(services, rows) {
    const out = [];
    for (const [key, startIso, date, time, avail] of rows || []) {
      const svc = services[key];
      if (!svc) continue;
      out.push({
        variantId: `${key}|${startIso}`, service: key, type: svc.type, location: svc.location,
        date, time, startIso, start: Date.parse(startIso), available: !!avail, price: svc.regularPrice,
      });
    }
    return out.sort((x, y) => x.start - y.start || x.location.localeCompare(y.location));
  }

  // ── App ───────────────────────────────────────────────────────────────────
  class Booking {
    constructor(el) {
      this.el = el;
      this.app = el.querySelector('[data-elb-app]');
      this.target = el.querySelector('[data-elb-booking]') || el;
      this.pins = [...el.querySelectorAll('[data-elb-pin]')];
      const data = JSON.parse(el.querySelector('[data-elb-data]').textContent);
      this.settings = data.settings;
      this.api = String(this.settings.apiUrl || '').replace(/\/+$/, '');
      this.services = {};
      this.slots = [];
      this.loading = true;
      this.loadError = false;
      // Terminarten (Texte aus den Section-Einstellungen, Dauer/Preis kommen vom Termin-Dienst)
      this.types = {};
      for (const t of data.types || []) {
        this.types[t.type] = { type: t.type, title: t.title, description: t.description, duration: t.duration, mode: 'showroom' };
      }
      const rr = this.settings.rueckruf || {};
      this.types.RUECKRUF = {
        type: 'RUECKRUF', title: rr.title || 'Kostenloser Rückruf', description: rr.description || '',
        duration: rr.duration || 15, mode: 'phone',
      };
      this.video = this.settings.video && this.settings.video.show ? this.settings.video : null;
      this.blocks = data.locations;
      const lines = (v) => String(v || '').split('\n').map((x) => x.trim()).filter(Boolean);
      // Fragen wie in den bisherigen Google-Formularen – je Terminart
      this.questions = {
        BERATUNG: [
          { name: 'reason', prop: 'Was führt dich zu uns', label: 'Was führt dich zu uns?', kind: 'textarea', required: true,
            placeholder: 'z. B. Haarausfall, mehr Volumen, Typveränderung …' },
          { name: 'product', prop: 'Interesse an', label: 'Für welches Produkt interessierst du dich?', kind: 'select',
            options: lines(this.settings.beratungProducts) },
          { name: 'note', prop: 'Anmerkung', label: 'Möchtest du uns sonst noch etwas mitteilen? 😊', kind: 'textarea',
            placeholder: 'Wunschfarbe, Länge, Modellname, Fragen …' },
        ],
        RUECKRUF: [
          { name: 'topic', prop: 'Worum geht es', label: 'Worum geht es?', kind: 'select',
            options: lines(this.settings.rueckrufTopics) },
          { name: 'message', prop: 'Was möchtest du besprechen', label: 'Was möchtest du mit uns besprechen?', kind: 'textarea',
            placeholder: 'Damit wir uns gut vorbereiten können …' },
        ],
        SERVICE: [
          { name: 'concern', prop: 'Anliegen', label: 'Was ist dein Anliegen?', kind: 'select', required: true,
            options: lines(this.settings.serviceConcerns) },
          { name: 'details', prop: 'Was führt dich genau zu uns', label: 'Was führt dich genau zu uns?', kind: 'textarea',
            placeholder: 'Welche Wig, was soll angepasst werden …' },
        ],
      };
      this.state = { location: null, type: null, date: null, variantId: null, answers: {}, phone: '', busy: false, error: '', invalid: [] };
      this.monthOffset = 0;
      this.build();
      this.bind();
      this.render();
      this.load().then(() => { this.applyHash(false); this.render(); });
    }

    async load() {
      try {
        const sep = this.api.includes('?') ? '&' : '?';
        const all = await fetch(`${this.api}${sep}action=all`).then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
        if (all.error || !all.config) throw new Error(all.error || 'keine Daten');
        const cfg = all.config, data = all.slots;
        this.services = {};
        this.freeTypes = new Set();
        for (const svc of cfg.services) {
          this.services[svc.key] = svc;
          if (svc.price === 0 && svc.regularPrice > 0) this.freeTypes.add(svc.type);
          if (this.types[svc.type]) this.types[svc.type].duration = svc.duration;
        }
        this.cfg = cfg;
        this.slots = slotsFromApi(this.services, data.slots);
        this.loading = false;
        this.loadError = false;
      } catch (e) {
        console.error('[Terminbuchung]', e);
        this.loading = false;
        this.loadError = true;
      }
      this.build();
    }

    build() {
      const freeEnd = this.cfg && this.cfg.free ? Date.parse(this.cfg.freeUntil) : 0;
      this.freeNow = !!freeEnd && Date.now() <= freeEnd;
      this.freeEndLabel = freeEnd ? fmt({ day: 'numeric', month: 'long' }).format(freeEnd - 60e3) : '';

      // Standorte: Reihenfolge der Blöcke, plus Standorte, die nur in SKUs vorkommen
      const known = new Map();
      for (const b of this.blocks) if (b && b.name) known.set(b.name, { ...b, anchor: b.anchor || `termin-${slug(b.name)}` });
      for (const s of this.slots) if (!known.has(s.location)) known.set(s.location, { name: s.location, anchor: `termin-${slug(s.location)}` });
      this.locations = [...known.values()];

      const freeBanner = this.el.querySelector('[data-elb-free]');
      if (freeBanner) {
        freeBanner.hidden = !this.freeNow;
        freeBanner.textContent = this.freeNow
          ? `Aktuell kostenlos – bis ${this.freeEndLabel} buchst du deine Beratung ohne Gebühr.` : '';
      }
    }

    // Freie Zeiten neu laden (z. B. nachdem ein Termin gerade vergeben wurde)
    async refreshAvailability() {
      await this.load();
      const sel = this.slots.find((x) => x.variantId === this.state.variantId);
      if (!sel || !sel.available) this.state.variantId = null;
      this.render();
    }

    isFree(type, cents) {
      if (cents === 0) return true;
      return !!(this.freeTypes && this.freeTypes.has(type));
    }

    product(type) { return this.types[type] || { type, title: type, duration: 60, mode: 'showroom' }; }

    isRemote(name) {
      const loc = this.locations.find((l) => l.name === name);
      if (loc && loc.kind) return loc.kind === 'remote';
      const own = this.slots.filter((s) => s.location === name);
      return own.length > 0 && own.every((s) => this.product(s.type).mode === 'phone');
    }

    priceOf(type, location) {
      const list = this.slotsFor({ location, type });
      return list.length ? Math.min(...list.map((s) => s.price)) : 0;
    }

    slotsFor({ location = this.state.location, type = this.state.type, date } = {}) {
      return this.slots.filter((s) => (!location || s.location === location) && (!type || s.type === type) && (!date || s.date === date));
    }

    typesAt(location) {
      const types = [];
      for (const t of Object.keys(this.types)) if (this.slotsFor({ location, type: t }).some((s) => s.available)) types.push(t);
      return types;
    }

    // ── Auswahl ──────────────────────────────────────────────────────────────
    selectLocation(name, scroll) {
      if (this.state.location !== name) {
        Object.assign(this.state, { location: name, type: null, date: null, variantId: null, error: '' });
        this.monthOffset = 0;
      }
      const types = this.typesAt(name);
      if (types.length === 1) this.state.type = types[0];
      else if (types.includes('BERATUNG') && !this.state.type) this.state.type = 'BERATUNG';
      this.autoDate();
      this.render();
      if (scroll) this.scrollTo('[data-step="date"]');
    }

    selectType(type) {
      Object.assign(this.state, { type, date: null, variantId: null, error: '', invalid: [] });
      this.monthOffset = 0;
      this.autoDate();
      this.render();
    }

    autoDate() {
      if (!this.state.type) return;
      const all = this.slotsFor();
      const first = all.find((s) => s.available);
      this.state.date = first ? first.date : null;
      // auf den Monat des ersten freien Tages springen
      const monthKeys = [...new Set(all.map((s) => s.date.slice(0, 7)))];
      this.monthOffset = first ? Math.max(0, monthKeys.indexOf(first.date.slice(0, 7))) : 0;
    }

    applyHash(scroll) {
      const h = decodeURIComponent((location.hash || '').slice(1));
      const q = new URLSearchParams(location.search).get('standort');
      const hit = this.locations.find((l) => l.anchor === h || (q && slug(l.name) === slug(q)));
      if (hit) {
        this.selectLocation(hit.name, false);
        if (scroll) this.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    }

    scrollTo(sel) {
      const n = this.app.querySelector(sel);
      if (!n) return;
      const r = n.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.7) n.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    bind() {
      this.app.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]');
        if (!b || b.disabled) return;
        const { act, val } = b.dataset;
        if (act === 'loc') this.selectLocation(val, true);
        if (act === 'type') this.selectType(val);
        if (act === 'date') { Object.assign(this.state, { date: val, variantId: null, error: '' }); this.render(); this.scrollTo('[data-step="time"]'); }
        if (act === 'time') { Object.assign(this.state, { variantId: val, error: '' }); this.render(); this.scrollTo('[data-step="confirm"]'); }
        if (act === 'month') { this.monthOffset = Math.max(0, this.monthOffset + Number(val)); this.render(); }
        if (act === 'book') this.book();
      });
      this.app.addEventListener('input', (e) => {
        const n = e.target.name;
        if (!n) return;
        if (n === 'phone') this.state.phone = e.target.value;
        else this.state.answers[n] = e.target.value;
        if (this.state.invalid.includes(n) && String(e.target.value).trim()) {
          this.state.invalid = this.state.invalid.filter((x) => x !== n);
          e.target.removeAttribute('aria-invalid');
          e.target.closest('.elb__field')?.classList.remove('is-invalid');
          if (!this.state.invalid.length && this.state.error) {
            this.state.error = '';
            this.app.querySelectorAll('.elb__step--confirm .elb__error').forEach((n) => n.remove());
          }
        }
      });
      window.addEventListener('hashchange', () => this.applyHash(true));
      // Bestehende Links/Karten auf der Seite (#termin-berlin usw.) abfangen
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href*="#termin-"]');
        if (!a) return;
        const anchor = a.getAttribute('href').split('#')[1];
        const hit = this.locations.find((l) => l.anchor === anchor);
        if (!hit) return;
        e.preventDefault();
        history.replaceState(null, '', `#${anchor}`);
        this.selectLocation(hit.name, false);
        this.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // ── Buchen ───────────────────────────────────────────────────────────────
    async book() {
      const slot = this.slots.find((s) => s.variantId === this.state.variantId);
      if (!slot || this.state.busy) return;
      // Pflichtfelder prüfen
      const qs = this.questions[slot.type] || [];
      const invalid = qs.filter((q) => q.required && !String(this.state.answers[q.name] || '').trim()).map((q) => q.name);
      if (this.state.phone.replace(/\D/g, '').length < 6) invalid.unshift('phone');
      if (invalid.length) {
        Object.assign(this.state, { invalid, error: 'Bitte fülle die markierten Felder aus.' });
        this.render();
        const first = this.app.querySelector(`[name="${invalid[0]}"]`);
        if (first) first.focus();
        return;
      }
      this.state.busy = true; this.state.error = ''; this.state.invalid = []; this.render();
      const loc = this.locations.find((l) => l.name === slot.location) || {};
      const prod = this.product(slot.type) || {};
      const remote = prod.mode === 'phone';
      const properties = {
        Termin: `${fShortDate.format(slot.start)}, ${slot.time} Uhr`,
        Standort: remote ? 'Telefonischer Rückruf' : [slot.location, loc.address].filter(Boolean).join(', '),
        Dauer: `${prod.duration || 60} Min.`,
      };
      properties.Telefon = this.state.phone.trim().slice(0, 40);
      const answers = {};
      for (const q of qs) {
        const v = String(this.state.answers[q.name] || '').trim();
        if (v) { properties[q.prop] = v.slice(0, 500); answers[q.prop] = v.slice(0, 500); }
      }
      const taken = (msg) => {
        Object.assign(this.state, { variantId: null, busy: false, error: msg });
        this.refreshAvailability().then(() => this.scrollTo('[data-step="time"]'));
      };

      try {
        // 1) Termin im Kalender reservieren (verhindert Doppelbuchungen während des Checkouts)
        // text/plain: ohne CORS-Vorabanfrage (nötig für Google Apps Script)
        const res = await fetch(this.api, {
          method: 'POST', headers: { 'content-type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ action: 'hold', service: slot.service, start: slot.startIso, phone: properties.Telefon, answers }),
        });
        const hold = await res.json().catch(() => ({}));
        const status = hold.status || res.status;
        if (status === 409) return taken(hold.error || 'Dieser Termin wurde gerade vergeben. Bitte wähle eine andere Uhrzeit.');
        if (status !== 200 || !hold.bookingId) throw new Error(hold.error || status);

        // 2) Andere Termine aus dem Warenkorb entfernen, damit nur ein Termin gebucht wird
        const bookingVariants = new Set(Object.values(this.services).map((x) => x.variantId).filter(Boolean));
        const cart = await fetch(`${root}cart.js`).then((r) => r.json());
        const updates = {};
        for (const item of cart.items || []) if (bookingVariants.has(item.variant_id)) updates[item.key] = 0;
        if (Object.keys(updates).length) {
          await fetch(`${root}cart/update.js`, {
            method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ updates }),
          });
        }
        // 3) In den Warenkorb und weiter zur Kasse
        properties._booking_id = hold.bookingId;
        properties._start = slot.startIso;
        const add = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: hold.variantId, quantity: 1, properties }] }),
        });
        if (!add.ok) throw new Error(`cart ${add.status}`);
        window.location.href = this.settings.goToCheckout ? `${root}checkout` : `${root}cart`;
      } catch (e) {
        console.error('[Terminbuchung]', e);
        Object.assign(this.state, { busy: false, error: (e && e.message && !/^\d+$/.test(e.message) && !/^cart/.test(e.message)) ? e.message : 'Da ist etwas schiefgelaufen. Bitte versuche es noch einmal oder ruf uns an.' });
        this.render();
      }
    }

    // ── Darstellung ──────────────────────────────────────────────────────────
    render() {
      const st = this.state;
      if (this.loading || this.loadError) {
        const phone = this.settings.phoneUrl ? ` <a href="${esc(this.settings.phoneUrl)}">Ruf uns gerne an.</a>` : '';
        this.app.innerHTML = this.loading
          ? '<div class="elb__loading">Termine werden geladen …</div>'
          : `<p class="elb__error">Die Termine konnten gerade nicht geladen werden. Bitte lade die Seite neu.${phone}</p>`;
        this.updatePins();
        return;
      }
      const html = [this.renderLocations()];
      if (st.location) html.push(this.renderTypes());
      if (st.location && st.type) html.push(this.renderDates());
      if (st.date) html.push(this.renderTimes());
      if (st.variantId) html.push(this.renderConfirm());
      html.push(this.renderPhone());
      const active = document.activeElement && this.app.contains(document.activeElement)
        ? { name: document.activeElement.name, pos: document.activeElement.selectionStart } : null;
      this.app.innerHTML = html.join('');
      this.updatePins();
      if (active && active.name) {
        const n = this.app.querySelector(`[name="${active.name}"]`);
        if (n) { n.focus(); try { n.setSelectionRange(active.pos, active.pos); } catch (e) { /* select */ } }
      }
    }

    // Karten-Pins: gewählten Standort hervorheben, Standorte ohne freie Termine abschwächen
    updatePins() {
      for (const pin of this.pins) {
        const name = pin.getAttribute('data-elb-pin');
        const open = this.slots.some((s) => s.location === name && s.available);
        pin.classList.toggle('is-on', this.state.location === name);
        pin.classList.toggle('is-empty', !open);
      }
    }

    step(n, key, title, body, extraClass = '') {
      return `<section class="elb__step ${extraClass}" data-step="${key}">
        <h3 class="elb__step-title"><span class="elb__num">${n}</span>${esc(title)}</h3>${body}</section>`;
    }

    renderLocations() {
      const cards = this.locations.map((l) => {
        const open = this.slotsFor({ location: l.name, type: null }).filter((s) => s.available);
        const next = open[0];
        const on = this.state.location === l.name;
        const meta = next
          ? `Nächster Termin: ${fWeekdayShort.format(next.start)} ${fDay.format(next.start)}. ${fMonthShort.format(next.start)}`
          : 'Derzeit keine freien Termine';
        return `<button type="button" class="elb__loc${on ? ' is-on' : ''}" data-act="loc" data-val="${esc(l.name)}" aria-pressed="${on}" ${next ? '' : 'disabled'} id="${esc(l.anchor)}">
          ${l.image ? `<span class="elb__loc-img" style="background-image:url('${esc(l.image)}')"></span>` : ''}
          <span class="elb__loc-body">
            <span class="elb__loc-name">${esc(l.title || l.name)}</span>
            ${l.note ? `<span class="elb__loc-note">${esc(l.note)}</span>` : ''}
            <span class="elb__loc-meta">${esc(meta)}</span>
          </span></button>`;
      }).join('');
      return this.step(1, 'location', 'Wo möchtest du beraten werden?', `<div class="elb__locs">${cards}</div>`);
    }

    renderTypes() {
      const types = this.typesAt(this.state.location);
      const remote = this.isRemote(this.state.location);
      const video = remote && this.video ? this.videoCard() : '';
      if (types.length <= 1 && this.state.type) {
        const p = this.product(this.state.type);
        const single = !video;
        return this.step(2, 'type', video ? 'Wie möchtest du beraten werden?' : 'Dein Termin',
          `<div class="elb__types">${this.typeCard(p, true, single)}${video}</div>`);
      }
      if (!types.length) {
        return this.step(2, 'type', 'Dein Termin', `<p class="elb__empty">${remote ? 'Gerade sind alle Rückruftermine vergeben.' : 'An diesem Standort sind gerade alle Termine vergeben.'} Schau bald wieder vorbei oder ruf uns an.</p>${video ? `<div class="elb__types">${video}</div>` : ''}`);
      }
      const cards = types.map((t) => this.typeCard(this.product(t), this.state.type === t, false)).join('');
      return this.step(2, 'type', remote ? 'Wie möchtest du beraten werden?' : 'Was dürfen wir für dich tun?', `<div class="elb__types">${cards}${video}</div>`);
    }

    // Videoberatung: nur Info-Kachel, nicht anklickbar
    videoCard() {
      const v = this.video;
      return `<div class="elb__type is-disabled" aria-disabled="true">
        <span class="elb__type-head"><span class="elb__type-name">${esc(v.title || 'Beratung per Video')}</span><span class="elb__badge">${esc(v.status || 'Aktuell nicht verfügbar')}</span></span>
        ${v.text ? `<span class="elb__type-text">${esc(v.text)}</span>` : ''}
        <span class="elb__type-meta"><span>${esc(v.duration || 15)} Min.</span><span>Online / Video</span></span>
      </div>`;
    }

    priceHtml(type, cents) {
      if (cents === 0) return '<span class="elb__price"><strong>kostenlos</strong></span>';
      if (this.isFree(type)) {
        return `<span class="elb__price"><s>${money(cents)}</s> <strong>kostenlos</strong></span>`;
      }
      return `<span class="elb__price"><strong>${money(cents)}</strong></span>`;
    }

    typeCard(p, on, single) {
      const price = this.priceOf(p.type, this.state.location);
      const tag = single ? 'div' : 'button';
      const attrs = single ? '' : ` type="button" data-act="type" data-val="${p.type}" aria-pressed="${on}"`;
      return `<${tag} class="elb__type${on ? ' is-on' : ''}${single ? ' is-single' : ''}"${attrs}>
        <span class="elb__type-head"><span class="elb__type-name">${esc(p.title)}</span>${this.priceHtml(p.type, price)}</span>
        ${p.description ? `<span class="elb__type-text">${esc(p.description)}</span>` : ''}
        <span class="elb__type-meta"><span>${esc(p.duration)} Min.</span><span>${p.mode === 'phone' ? 'Telefonisch' : 'Vor Ort im Showroom'}</span></span>
      </${tag}>`;
    }

    renderDates() {
      const all = this.slotsFor();
      const dates = [...new Set(all.map((s) => s.date))];
      if (!dates.length) {
        return this.step(3, 'date', 'Wähle einen Tag', '<p class="elb__empty">Für diese Auswahl sind gerade keine Termine frei.</p>');
      }
      // nach Monaten gruppieren, Monatsweise blättern
      const months = [];
      for (const d of dates) {
        const key = d.slice(0, 7);
        if (!months.length || months[months.length - 1].key !== key) months.push({ key, dates: [] });
        months[months.length - 1].dates.push(d);
      }
      this.monthOffset = Math.min(this.monthOffset, months.length - 1);
      const m = months[this.monthOffset];
      const chips = m.dates.map((d) => {
        const open = all.filter((s) => s.date === d && s.available).length;
        const t = noon(d);
        const on = this.state.date === d;
        return `<button type="button" class="elb__date${on ? ' is-on' : ''}" data-act="date" data-val="${d}" aria-pressed="${on}" ${open ? '' : 'disabled'}
          aria-label="${esc(fFull.format(t))}, ${open ? `${open} freie Zeiten` : 'ausgebucht'}">
          <span class="elb__date-wd">${esc(fWeekdayShort.format(t).replace('.', ''))}</span>
          <span class="elb__date-d">${esc(fDay.format(t))}</span>
          <span class="elb__date-m">${esc(fMonthShort.format(t).replace('.', ''))}</span>
          <span class="elb__date-n">${open ? `${open} frei` : 'ausgebucht'}</span>
        </button>`;
      }).join('');
      const nav = months.length > 1 ? `<div class="elb__month">
          <button type="button" class="elb__arrow" data-act="month" data-val="-1" ${this.monthOffset === 0 ? 'disabled' : ''} aria-label="Vorheriger Monat">‹</button>
          <span class="elb__month-name">${esc(fMonthLong.format(noon(m.dates[0])))}</span>
          <button type="button" class="elb__arrow" data-act="month" data-val="1" ${this.monthOffset >= months.length - 1 ? 'disabled' : ''} aria-label="Nächster Monat">›</button>
        </div>` : `<div class="elb__month"><span class="elb__month-name">${esc(fMonthLong.format(noon(m.dates[0])))}</span></div>`;
      return this.step(3, 'date', 'Wähle einen Tag', `${nav}<div class="elb__dates">${chips}</div>`);
    }

    renderTimes() {
      const list = this.slotsFor({ date: this.state.date });
      const chips = list.map((s) => {
        const on = this.state.variantId === s.variantId;
        return `<button type="button" class="elb__time${on ? ' is-on' : ''}" data-act="time" data-val="${s.variantId}" aria-pressed="${on}" ${s.available ? '' : 'disabled'}
          ${s.available ? '' : 'aria-label="' + s.time + ' Uhr, vergeben"'}>${s.time}</button>`;
      }).join('');
      const err = this.state.error && !this.state.variantId ? `<p class="elb__error" role="alert">${esc(this.state.error)}</p>` : '';
      return this.step(4, 'time', `Uhrzeit am ${fFull.format(noon(this.state.date))}`, `${err}<div class="elb__times">${chips}</div>`);
    }

    renderConfirm() {
      const s = this.slots.find((x) => x.variantId === this.state.variantId);
      if (!s) return '';
      const p = this.product(s.type);
      const loc = this.locations.find((l) => l.name === s.location) || {};
      const free = this.isFree(s.type, s.price);
      const remote = p.mode === 'phone';
      const field = (q) => {
        const bad = this.state.invalid.includes(q.name);
        const val = this.state.answers[q.name] || '';
        const opt = q.required ? '' : ' <em>optional</em>';
        const aria = `${q.required ? ' required aria-required="true"' : ''}${bad ? ' aria-invalid="true"' : ''}`;
        let control;
        if (q.kind === 'select') {
          control = `<select name="${q.name}"${aria}><option value="">Bitte wählen</option>${q.options.map((o) => `<option${o === val ? ' selected' : ''}>${esc(o)}</option>`).join('')}</select>`;
        } else {
          control = `<textarea name="${q.name}" rows="3" maxlength="500"${aria} placeholder="${esc(q.placeholder || '')}">${esc(val)}</textarea>`;
        }
        return `<label class="elb__field${q.kind === 'textarea' ? ' elb__field--wide' : ''}${bad ? ' is-invalid' : ''}"><span>${esc(q.label)}${opt}</span>${control}</label>`;
      };
      const qs = (this.questions[s.type] || []).filter((q) => q.kind !== 'select' || q.options.length)
        .sort((a, b) => (a.kind === 'select' ? 0 : 1) - (b.kind === 'select' ? 0 : 1));
      const phoneBad = this.state.invalid.includes('phone');
      const phone = `<label class="elb__field${phoneBad ? ' is-invalid' : ''}"><span>${remote ? 'Unter welcher Nummer rufen wir dich an?' : 'Telefonnummer'}</span>
          <input type="tel" name="phone" autocomplete="tel" inputmode="tel" required aria-required="true"${phoneBad ? ' aria-invalid="true"' : ''} value="${esc(this.state.phone)}" placeholder="${remote ? 'z. B. 0170 1234567' : 'Für Rückfragen zu deinem Termin'}"></label>`;
      const btn = this.state.busy ? 'Einen Moment …'
        : (remote ? 'Rückruf kostenlos buchen' : (free ? 'Termin kostenlos buchen' : `Weiter zur Zahlung · ${money(s.price)}`));
      const where = remote
        ? 'Telefonisch – wir rufen dich zur gewählten Zeit an.'
        : `${esc(s.location)}${loc.address ? `, ${esc(loc.address)}` : ''}${loc.note ? `<br><span class="elb__muted">${esc(loc.note)}</span>` : ''}${loc.mapsUrl ? ` <a href="${esc(loc.mapsUrl)}" target="_blank" rel="noopener">Karte</a>` : ''}`;
      return this.step(5, 'confirm', 'Fast geschafft', `
        <div class="elb__summary">
          <dl>
            <div><dt>Termin</dt><dd>${esc(p.title)}</dd></div>
            <div><dt>Wann</dt><dd>${esc(fFull.format(s.start))}, ${s.time} Uhr <span class="elb__muted">(${esc(p.duration)} Min.)</span></dd></div>
            <div><dt>${remote ? 'Wie' : 'Wo'}</dt><dd>${where}</dd></div>
            <div><dt>Preis</dt><dd>${this.priceHtml(s.type, s.price)}</dd></div>
          </dl>
        </div>
        <div class="elb__fields">${phone}${qs.map(field).join('')}</div>
        ${this.state.error ? `<p class="elb__error" role="alert">${esc(this.state.error)}</p>` : ''}
        <button type="button" class="elb__cta" data-act="book" ${this.state.busy ? 'disabled' : ''}>${esc(btn)}</button>
        <p class="elb__fine">${free
          ? 'Name und E-Mail gibst du im nächsten Schritt an – es fallen keine Kosten an. Die Bestätigung kommt per E-Mail.'
          : `Sichere Zahlung im nächsten Schritt. ${esc(this.typeNote(s.type))} ${esc(this.settings.noShowText || '')}`}</p>`, 'elb__step--confirm');
    }

    typeNote(type) {
      return type === 'BERATUNG' ? (this.settings.beratungCredit || '') : (this.settings.serviceNote || '');
    }

    // Hinweis-Kachel unten: wählt den Rückruf vor (oder Link, falls keine Rückruftermine da sind)
    renderPhone() {
      const remoteLoc = this.locations.find((l) => this.isRemote(l.name) && this.slotsFor({ location: l.name, type: null }).some((x) => x.available));
      const title = esc(this.settings.phoneTitle || 'Lieber telefonisch?');
      const text = esc(this.settings.phoneText || '');
      const inner = `<span class="elb__phone-title">${title}</span><span class="elb__phone-text">${text}</span><span class="elb__phone-arrow" aria-hidden="true">→</span>`;
      if (remoteLoc) {
        if (this.state.location === remoteLoc.name) return '';
        return `<button type="button" class="elb__phone" data-act="loc" data-val="${esc(remoteLoc.name)}">${inner}</button>`;
      }
      if (this.settings.phoneUrl) return `<a class="elb__phone" href="${esc(this.settings.phoneUrl)}">${inner}</a>`;
      return '';
    }
  }

  // ── Route planen: Auswahl der Karten-App ──────────────────────────────────
  // Webseiten können nicht erkennen, welche Apps installiert sind. Die Links sind
  // App-Links (Universal/App Links): Ist die App installiert, öffnet sie sich,
  // sonst die Website. Auf Android öffnet „Andere App wählen“ (geo:) die
  // System-Auswahl mit allen installierten Karten-Apps.
  const ua = navigator.userAgent || '';
  const isApple = /iPhone|iPad|iPod|Macintosh/.test(ua);
  const isAndroid = /Android/i.test(ua);

  function routeOptions(query) {
    const q = encodeURIComponent(query);
    const apple = { key: 'apple', label: 'Apple Karten', href: `https://maps.apple.com/?daddr=${q}&dirflg=d` };
    const google = { key: 'google', label: 'Google Maps', href: `https://www.google.com/maps/dir/?api=1&destination=${q}` };
    const waze = { key: 'waze', label: 'Waze', href: `https://waze.com/ul?q=${q}&navigate=yes` };
    const other = { key: 'other', label: 'Andere App wählen', href: `geo:0,0?q=${q}` };
    if (isApple) return [apple, google, waze];
    if (isAndroid) return [google, waze, other];
    return [google, apple, waze];
  }

  const ROUTE_ICONS = {
    apple: '<path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z"/><circle cx="12" cy="9.5" r="2.5"/>',
    google: '<path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20Z"/><path d="M9 4v13.5M15 6.5V20"/>',
    waze: '<path d="M5 15.5A7.5 7.5 0 1 1 19.5 12c0 3-2 5.5-5 5.5H9a4 4 0 0 1-4-2Z"/><circle cx="10" cy="17.5" r="1.6"/><circle cx="16" cy="17.5" r="1.6"/><path d="M10 10.5h.01M14.5 10.5h.01"/>',
    other: '<rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/>',
    copy: '<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/>',
  };
  const svgIcon = (k) => `<svg viewBox="0 0 24 24" aria-hidden="true">${ROUTE_ICONS[k]}</svg>`;
  let openRoute = null;

  function closeRoute(focusToggle) {
    if (!openRoute) return;
    const { wrap, toggle, menu } = openRoute;
    menu.remove();
    wrap.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    if (focusToggle) toggle.focus();
    openRoute = null;
  }

  function openRouteMenu(wrap, toggle) {
    closeRoute(false);
    const query = wrap.getAttribute('data-query') || '';
    const menu = document.createElement('div');
    menu.className = 'elb-route__menu';
    menu.setAttribute('role', 'menu');
    const id = `elb-route-${Math.random().toString(36).slice(2, 8)}`;
    menu.id = id;
    menu.innerHTML = `<p class="elb-route__title">Route öffnen mit</p>${routeOptions(query).map((o) => (
      `<a role="menuitem" class="elb-route__item" href="${esc(o.href)}"${o.key === 'other' ? '' : ' target="_blank" rel="noopener"'} data-key="${o.key}">${svgIcon(o.key)}<span>${esc(o.label)}</span></a>`
    )).join('')}<button type="button" role="menuitem" class="elb-route__item" data-copy>${svgIcon('copy')}<span>Adresse kopieren</span></button>`;
    wrap.appendChild(menu);
    wrap.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-controls', id);
    openRoute = { wrap, toggle, menu };
    const first = menu.querySelector('.elb-route__item');
    if (first) first.focus({ preventScroll: true });

    menu.addEventListener('click', async (e) => {
      const copy = e.target.closest('[data-copy]');
      if (copy) {
        const label = copy.querySelector('span');
        try {
          await navigator.clipboard.writeText(query);
          label.textContent = 'Adresse kopiert ✓';
        } catch (err) {
          label.textContent = query;
        }
        setTimeout(() => closeRoute(true), 1400);
        return;
      }
      if (e.target.closest('a')) setTimeout(() => closeRoute(false), 50);
    });
    menu.addEventListener('keydown', (e) => {
      const items = [...menu.querySelectorAll('.elb-route__item')];
      const i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length].focus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length].focus(); }
      if (e.key === 'Tab' && !e.shiftKey && i === items.length - 1) closeRoute(false);
    });
  }

  function initRoutes(scope) {
    (scope || document).querySelectorAll('[data-elb-route]').forEach((wrap) => {
      if (wrap.__elbRoute) return;
      wrap.__elbRoute = true;
      const toggle = wrap.querySelector('[data-elb-route-toggle]');
      if (!toggle) return;
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('aria-haspopup', 'menu');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (openRoute && openRoute.wrap === wrap) closeRoute(false);
        else openRouteMenu(wrap, toggle);
      });
    });
  }
  document.addEventListener('click', (e) => {
    if (openRoute && !openRoute.wrap.contains(e.target)) closeRoute(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openRoute) closeRoute(true);
  });

  function init(scope) {
    (scope || document).querySelectorAll('[data-elb]').forEach((el) => {
      if (el.__elb) return;
      try { el.__elb = new Booking(el); } catch (e) {
        console.error('[Terminbuchung]', e);
        const app = el.querySelector('[data-elb-app]');
        if (app) app.innerHTML = '<p class="elb__error">Die Terminbuchung konnte nicht geladen werden. Bitte ruf uns an.</p>';
      }
    });
  }
  const start = (scope) => { init(scope); initRoutes(scope); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start());
  else start();
  // Theme-Editor: Section neu laden
  document.addEventListener('shopify:section:load', (e) => start(e.target));
})();
